import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const resetSchema = z.object({
    email: z.string().email('Email invalide'),
});

type ResetFormData = z.infer<typeof resetSchema>;

interface PasswordResetFormProps {
    onBackToLogin: () => void;
}

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onBackToLogin }) => {
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState(false);
    const { resetPassword, isLoading } = useAuth();

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm<ResetFormData>({
        resolver: zodResolver(resetSchema),
    });

    const onSubmit = async (data: ResetFormData) => {
        setError('');
        setSuccess(false);

        const result = await resetPassword(data.email);

        if (result.success) {
            setSuccess(true);
        } else {
            setError(result.error || 'Une erreur est survenue. Veuillez réessayer.');
        }
    };

    if (success) {
        return (
            <div className="space-y-6">
                <div className="text-center">
                    <div className="w-16 h-16 bg-n8n-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="h-8 w-8 text-n8n-green" />
                    </div>
                    <h2 className="text-3xl font-bold text-light-text-primary mb-2">
                        Email envoyé !
                    </h2>
                    <p className="text-light-text-muted">
                        Nous avons envoyé un lien de réinitialisation à{' '}
                        <span className="font-medium text-light-text-secondary">{getValues('email')}</span>
                    </p>
                </div>

                <div className="bg-dark-bg-tertiary rounded-xl p-4 border border-gray-600">
                    <p className="text-sm text-light-text-muted">
                        <strong className="text-light-text-secondary">Note :</strong> Si vous ne recevez pas l'email dans les prochaines minutes, vérifiez votre dossier spam.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={onBackToLogin}
                    className="w-full flex items-center justify-center gap-2 text-n8n-purple hover:text-n8n-blue transition-colors font-medium"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Retour à la connexion
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <button
                    type="button"
                    onClick={onBackToLogin}
                    className="flex items-center gap-2 text-light-text-muted hover:text-light-text-primary transition-colors mb-4"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Retour
                </button>
                <h2 className="text-3xl font-bold text-light-text-primary mb-2">
                    Réinitialiser le mot de passe
                </h2>
                <p className="text-light-text-muted">
                    Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
                </p>
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                    <label htmlFor="reset-email" className="block text-sm font-medium text-light-text-secondary mb-2">
                        Adresse email
                    </label>
                    <div className="relative">
                        <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-light-text-muted h-5 w-5" />
                        <input
                            {...register('email')}
                            type="email"
                            id="reset-email"
                            className="input-field pl-12"
                            placeholder="votre@email.com"
                            autoFocus
                        />
                    </div>
                    {errors.email && (
                        <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full"
                >
                    {isLoading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
                </button>
            </form>

            <div className="text-center">
                <p className="text-sm text-light-text-muted">
                    Vous avez retrouvé votre mot de passe ?{' '}
                    <button
                        type="button"
                        onClick={onBackToLogin}
                        className="text-n8n-purple hover:text-n8n-blue font-medium transition-colors"
                    >
                        Se connecter
                    </button>
                </p>
            </div>
        </div>
    );
};

export default PasswordResetForm;
